import {
  QuestionCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button } from "syngenta-digital-cropwise-react-ui-kit";
import { type TrialDataInterface } from "./types";
import { Popconfirm, Switch, Dropdown, type MenuProps } from "antd";
import { useTranslation } from "react-i18next";
import {
  TrialAmplitudeEvent,
  TrialReportAmplitudeEvent,
} from "../../shared/utils/amplitudeEvents";
import { formatTranslation } from "../../shared/utils/translationUtils";
import useAxios from "../../shared/custom-hooks/useAxios";
import { ReactComponent as MenuIcon } from "../../assets/images/Menu.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/archive.svg";
import { ReactComponent as EditIcon } from "../../assets/images/edit-hover.svg";
import { ReactComponent as DelIcon } from "../../assets/images/delete.svg"; // Import the SVG icon
import { formatDate } from "../create-protocol/utils";
import { useNewTrialState } from "../new-trail/hooks/NewTrialStateProvider";
import { useTrialsRouter } from "../../shared/custom-hooks/useTrialsRouter";
import { useTrackingEvent } from "../../shared/custom-hooks/useTracking";

export const TrialListColumns = (
  handleDeleteTrialData: any,
  openModal: any,
  openDrawer: any,
  toggleCompletion: any,
  handleShareReport: any
): any => {
  const { t } = useTranslation();
  const { router, farmId } = useTrialsRouter();
  const trackTrialEvents = useTrackingEvent({
    page: TrialAmplitudeEvent.TRIALS_PAGE,
    group: TrialAmplitudeEvent.TRIALS_GROUP,
    module: TrialAmplitudeEvent.TRIALS_GROUP,
  });
  useNewTrialState();
  const handleNavigation = (id: string, isCompleted?: boolean): void => {
    router.goToTrialsEditPage(undefined, id, false, isCompleted === true);
  };

  const { response: farmProperties } = useAxios(
    `/properties/${farmId as string}/fields?attributes=geometry`,
    true
  );

  const fieldData = Array.isArray(farmProperties?.content)
    ? farmProperties?.content
    : [];

  const getFieldName = (fieldId: string): string => {
    return fieldData?.length > 0
      ? fieldData?.find((item: any) => item?.id?.includes(fieldId))?.name
      : "";
  };

  const TRIAL_COLUMNS = [
    {
      title: formatTranslation(t("trials.tableTitle1")),
      dataIndex: "name",
      key: "name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        a.name.localeCompare(b.name),
      render: (text: string) => <span>{text}</span>,
    },
    {
      title: formatTranslation(t("trials.tableTitle4")),
      dataIndex: "field_name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        a?.field_name?.localeCompare(b?.field_name),
      render: (_a: any, record: any) => {
        return (
          <span data-testid="field-name">
            {record?.field_ids.length && getFieldName(record?.field_ids[0])}
          </span>
        );
      },
      key: "field_name",
    },
    {
      title: formatTranslation(t("createProtocol.basicInfo.protocolCrop")),
      dataIndex: "crop",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        a.crop.localeCompare(b.crop),
      render: (text: string) => <span>{text}</span>,
      key: "crop",
    },
    {
      title: formatTranslation(t("trials.tableTitle5")),
      dataIndex: "start_date",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        new Date(b.start_date).getTime() - new Date(a.start_date).getTime(),
      render: (text: string) => <span>{formatDate(text)}</span>,
      key: "start_date",
    },
    {
      title: formatTranslation(t("trials.tableTitle6")),
      dataIndex: "end_date",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        new Date(b.end_date).getTime() - new Date(a.end_date).getTime(),
      key: "end_date",
      render: (text: string) => <span>{formatDate(text)}</span>,
    },
    {
      title: formatTranslation(t("trials.tableTitle2")),
      dataIndex: "protocol_name",
      key: "protocol_name",
      sorter: (a: TrialDataInterface, b: TrialDataInterface) =>
        a?.protocol_name.localeCompare(b?.protocol_name),
      render: (_: any, record: any) => (
        <span
          className="protocol-name"
          data-id={record?.id}
          onClick={() => record?.id && openDrawer(record?.protocol_id)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              record?.id && openDrawer(record?.protocol_id);
            }
          }}
          tabIndex={0}
          role="button"
        >
          {record?.protocol_name}
        </span>
      ),
    },
    {
      title: formatTranslation(t("trials.completion")),
      key: "completion",
      render: (_: any, record: any) => (
        <Switch
          checkedChildren={<CheckOutlined />}
          unCheckedChildren={<CloseOutlined />}
          onChange={(checked: boolean) => toggleCompletion(checked, record?.id)}
          checked={!!record?.completion}
        />
      ),
    },
    {
      title: formatTranslation(t("trials.tableTitle7")),
      key: "action",
      render: (_: any, record: any) => {
        const menuItems: MenuProps["items"] = [
          {
            key: "share",
            label: (
              <Button
                data-testid="export-btn"
                className="share-report-icon"
                disabled={record?.is_exportable === false}
                onClick={() => {
                  handleShareReport(record);
                  void trackTrialEvents({
                    event: TrialReportAmplitudeEvent.SHARE_TRIAL_REPORT,
                    originAction: "Share Report",
                  });
                }}
              >
                <FileTextOutlined className="share-icon" /> Share Report
              </Button>
            ),
          },
          {
            key: "export",
            label: (
              <Button
                data-testid="export-btn"
                className={record?.is_exportable ? "" : "export-workorder-icon"}
                disabled={record?.is_exportable === false}
                onClick={(): void => {
                  void trackTrialEvents({
                    event: TrialAmplitudeEvent.DOWNLOAD_WORK_ORDER,
                    originAction: "Export",
                  });
                  openModal(record?.id, record?.field_ids[0], record);
                }}
              >
                <DownloadIcon /> Export
              </Button>
            ),
          },
          {
            key: "edit",
            label: (
              <Button
                data-testid="edit-btn"
                onClick={() => {
                  void trackTrialEvents({
                    event: TrialAmplitudeEvent.EDIT_TRIAL,
                    originAction: "edit-trial",
                  });
                  handleNavigation(record?.id, record?.completion);
                }}
              >
                <EditIcon width={18} height={18} /> Edit
              </Button>
            ),
          },
          {
            key: "delete",
            label: (
              <Popconfirm
                rootClassName="trial-delete-popup"
                title={`Delete the ${record.name as string}?`}
                description={
                  <span className="del-label">
                    {formatTranslation(t("trials.deleteText"))}
                  </span>
                }
                icon={<QuestionCircleOutlined />}
                onConfirm={() => handleDeleteTrialData(record?.id)}
                onCancel={() => {}}
              >
                <Button className="del-btn" data-testid="delete-btn">
                  <DelIcon width={18} /> Delete
                </Button>
              </Popconfirm>
            ),
          },
        ];
        return (
          <Dropdown
            menu={{
              items: menuItems,
              className: "action-menu",
            }}
            trigger={["click"]}
            placement="bottomRight"
          >
            <Button className="action-btn" data-testid="menu-icon">
              <span>
                <MenuIcon className="menu-icon" />
              </span>
              {/* Use the imported SVG icon */}
            </Button>
          </Dropdown>
        );
      },
    },
  ];
  return TRIAL_COLUMNS;
};
